import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { fmtDateTime } from "@/lib/datetime";
import { fmtDistance, fmtEta } from "@/lib/units";
import { Truck, ChevronRight } from "lucide-react";

const ROUTE_STATUS_META = {
  planned:     { label: "NOT STARTED", cls: "bg-[#fef9c3] text-[#854D0E]" },
  in_progress: { label: "IN PROGRESS", cls: "bg-[#e0f0ff] text-[#1f4ea3]" },
  paused:      { label: "PAUSED",      cls: "bg-[#ffedd5] text-[#9A3412]" },
  completed:   { label: "COMPLETE",    cls: "bg-[#dcfce7] text-[#166534]" },
};

export default function DriverRoutesList() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => {
      api.get("/routes", { params: { mine: true } })
        .then((r) => setRoutes(r.data))
        .finally(() => setLoading(false));
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto pb-24" data-testid="driver-routes-list-page">
      <header className="mb-5">
        <h1 className="text-2xl lg:text-3xl font-display font-bold tracking-tight">My routes</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {loading ? "Loading…" :
            routes.length === 0 ? "No routes assigned to you yet." :
            `${routes.length} route${routes.length === 1 ? "" : "s"} · newest first`
          }
        </p>
      </header>

      {!loading && routes.length === 0 && (
        <Card className="p-8 text-center rounded-xl border-border bg-card/80">
          <Truck className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Sit tight — your dispatcher will assign one shortly.</p>
        </Card>
      )}

      {routes.length > 0 && (
        <ul className="space-y-2" data-testid="driver-routes-list">
          {routes.map((r) => {
            const meta = ROUTE_STATUS_META[r.status] || {
              label: (r.status || "").toUpperCase(),
              cls: "bg-secondary text-muted-foreground",
            };
            return (
              <li key={r.id}>
                <Link
                  to={`/driver/route/${r.id}`}
                  className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card/80 hover:bg-secondary/40 transition-colors"
                  data-testid={`driver-route-card-${r.id}`}
                >
                  <div className="h-11 w-11 rounded-full grid place-items-center bg-primary/10 text-primary shrink-0">
                    <Truck className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {r.label || `Route ${r.id.slice(0, 6)}`}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono-num mt-0.5">
                      {fmtDateTime(r.created_at)} · {r.stops?.length || 0} stops · {fmtDistance(r.distance_km)} · ~{fmtEta(r.duration_min, r.stops?.length || 0)}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full ${meta.cls}`}
                    data-testid={`driver-route-card-status-${r.id}`}
                  >
                    {meta.label}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
